import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';

declare global {
  interface Window {
    _paq?: unknown[][];
  }
}

@Injectable({
  providedIn: 'root'
})
export class MatomoTrackingService {
  private readonly isBrowser: boolean;

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(PLATFORM_ID) platformId: object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  trackExposeModalOpened(estateId?: string | number | null, estateTitle?: string | null): void {
    this.trackEvent('Expose', 'modal_opened', this.buildEstateLabel(estateId, estateTitle));
  }

  trackExposeFormStarted(estateId?: string | number | null, estateTitle?: string | null): void {
    this.trackEvent('Expose', 'form_started', this.buildEstateLabel(estateId, estateTitle));
  }

  trackExposeSubmitInvalid(
    estateId: string | number | null | undefined,
    estateTitle: string | null | undefined,
    invalidFieldCount: number
  ): void {
    this.trackEvent('Expose', 'submit_invalid', this.buildEstateLabel(estateId, estateTitle), invalidFieldCount);
  }

  trackExposeSubmitAttempt(estateId?: string | number | null, estateTitle?: string | null): void {
    this.trackEvent('Expose', 'submit_attempt', this.buildEstateLabel(estateId, estateTitle));
  }

  trackExposeSubmitSuccess(estateId?: string | number | null, estateTitle?: string | null): void {
    this.trackEvent('Expose', 'submit_success', this.buildEstateLabel(estateId, estateTitle));
  }

  trackExposeSubmitError(
    estateId: string | number | null | undefined,
    estateTitle: string | null | undefined,
    reason: string
  ): void {
    this.trackEvent('Expose', 'submit_error', `${this.buildEstateLabel(estateId, estateTitle)} | ${reason}`);
  }

  trackFormModalOpened(formName: string, context?: string | null): void {
    this.trackEvent(this.formCategory(formName), 'modal_opened', this.buildFormLabel(formName, context));
  }

  trackFormStarted(formName: string, context?: string | null): void {
    this.trackEvent(this.formCategory(formName), 'form_started', this.buildFormLabel(formName, context));
  }

  trackFormSubmitInvalid(formName: string, invalidFieldCount: number, context?: string | null): void {
    this.trackEvent(this.formCategory(formName), 'submit_invalid', this.buildFormLabel(formName, context), invalidFieldCount);
  }

  trackFormSubmitAttempt(formName: string, context?: string | null): void {
    this.trackEvent(this.formCategory(formName), 'submit_attempt', this.buildFormLabel(formName, context));
  }

  trackFormSubmitSuccess(formName: string, context?: string | null): void {
    this.trackEvent(this.formCategory(formName), 'submit_success', this.buildFormLabel(formName, context));
  }

  trackFormSubmitError(formName: string, reason: string, context?: string | null): void {
    this.trackEvent(this.formCategory(formName), 'submit_error', `${this.buildFormLabel(formName, context)} | ${reason}`);
  }

  trackEvent(category: string, action: string, name?: string, value?: number): void {
    const queue = this.getQueue();
    if (!queue) {
      return;
    }

    const command: unknown[] = ['trackEvent', category, action];
    if (name) {
      command.push(name);
    }
    if (typeof value === 'number') {
      if (!name) {
        command.push('');
      }
      command.push(value);
    }

    queue.push(command);
  }

  private getQueue(): unknown[][] | null {
    if (!this.isBrowser) {
      return null;
    }

    const currentWindow = this.document.defaultView;
    if (!currentWindow) {
      return null;
    }

    currentWindow._paq = currentWindow._paq || [];
    return currentWindow._paq;
  }

  private buildEstateLabel(estateId?: string | number | null, estateTitle?: string | null): string {
    const normalizedEstateId = this.normalizeEstateId(estateId);
    const idPart = normalizedEstateId ? `ID ${normalizedEstateId}` : 'ID unbekannt';
    const titlePart = estateTitle?.trim() || 'Ohne Titel';

    return `${idPart} | ${titlePart}`;
  }

  private buildFormLabel(formName: string, context?: string | null): string {
    const normalizedContext = context?.trim();
    return normalizedContext ? `${formName} | ${normalizedContext}` : formName;
  }

  private formCategory(formName: string): string {
    return `Form | ${formName}`;
  }

  private normalizeEstateId(estateId?: string | number | null): string {
    if (estateId === null || estateId === undefined) {
      return '';
    }

    return String(estateId).replace(/[^\d]/g, '');
  }
}
